import { useEffect, useMemo, useRef, useState, type ChangeEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  Building2,
  Camera,
  Check,
  Loader2,
  LogOut,
  Mail,
  MapPin,
  Pencil,
  ShieldCheck,
  X,
} from 'lucide-react';
import { useAuth } from '@/shared/hooks/useAuth';
import { Badge, Button, Chip, PageHeader } from '@/shared/ui';
import { absoluteAvatarUrl, userApi, type UpdateProfilePayload, type User } from '@/entities/user';
import { tagApi } from '@/entities/tag';
import { UserAvatar } from '@/entities/user';
import { useAuthStore } from '@/shared/stores/authStore';
import { ROUTES, QUERY_KEYS } from '@/shared/config/constants';
import { cn } from '@/shared/lib/cn';
import { formatPhoneBR, onlyDigits } from '@/shared/lib/phoneMask';
import { Skeleton } from '@/shared/ui';

const TIPO_PERFIL_OPTIONS = [
  { value: 'EMPRESARIO', label: 'Empresário' },
  { value: 'EXECUTIVO', label: 'Executivo' },
  { value: 'PROFISSIONAL', label: 'Profissional' },
  { value: 'CONSULTOR', label: 'Consultor' },
  { value: 'INVESTIDOR', label: 'Investidor' },
  { value: 'MENTOR', label: 'Mentor' },
] as const;

const ESTADOS_UF = [
  'AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MT', 'MS', 'MG',
  'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN', 'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO',
];

const MAX_TAGS = 3;

export const ProfilePage = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const setUser = useAuthStore((s) => s.setUser);
  const fotoUpdatedAt = useAuthStore((s) => s.user?.fotoUpdatedAt ?? null);
  const qc = useQueryClient();
  const [signingOut, setSigningOut] = useState(false);
  const [editing, setEditing] = useState<null | 'pessoal' | 'empresa' | 'tags' | 'bio'>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const profileQuery = useQuery({
    queryKey: QUERY_KEYS.ME,
    queryFn: userApi.getMe,
    staleTime: 60 * 1000,
  });
  const user = profileQuery.data?.user;

  const avatarMutation = useMutation({
    mutationFn: (file: File) => userApi.uploadAvatar(file),
    onSuccess: ({ user: next }) => {
      // Atualiza o cache do query *sincronamente* com a resposta do upload.
      // Sem isso, a UI dependeria do refetch async do invalidateQueries
      // (~200-400ms) e a foto so apareceria apos esse round-trip --
      // sintoma "so atualiza apos refresh".
      qc.setQueryData(QUERY_KEYS.ME, { user: next });
      // Auth store espelha os campos basicos pra avatar no header etc.
      // fotoUpdatedAt eh o cache-buster: absoluteAvatarUrl(url, version)
      // anexa ?v=<ts> e for ca o browser a refetchar mesmo quando o
      // backend serve a mesma URL apos um novo upload.
      setUser({
        id: next.id,
        email: next.email,
        nome: next.nome,
        fotoUrl: next.fotoUrl,
        fotoUpdatedAt: Date.now(),
        onboardingCompleto: next.onboardingCompleto,
        plano: next.plano,
      });
      // Background revalidation pra garantir consistencia caso outra request
      // tenha mudado o profile entre o upload e o setQueryData.
      void qc.invalidateQueries({ queryKey: QUERY_KEYS.ME });
      toast.success('Foto atualizada');
    },
    onError: (err: Error) => toast.error(err.message || 'Falha ao enviar foto'),
  });

  const handleAvatarChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) avatarMutation.mutate(file);
    e.target.value = '';
  };

  const handleLogout = async () => {
    setSigningOut(true);
    try {
      await logout();
      toast.success('Você saiu da sua conta.');
      navigate(ROUTES.LOGIN, { replace: true });
    } finally {
      setSigningOut(false);
    }
  };

  if (profileQuery.isLoading || !user) {
    return (
      <section
        className="mx-auto flex w-full max-w-2xl flex-col gap-5"
        aria-busy="true"
        aria-label="Carregando perfil"
      >
        <div className="flex flex-col gap-2">
          <Skeleton className="h-7 w-40" />
          <Skeleton className="h-3 w-72" />
        </div>
        <div className="overflow-hidden rounded-2xl bg-white p-6">
          <div className="flex flex-col items-center gap-3 text-center">
            <Skeleton className="size-20 rounded-full" />
            <Skeleton className="h-5 w-40" />
            <Skeleton className="h-3 w-32" />
            <div className="flex gap-2">
              <Skeleton className="h-6 w-16 rounded-full" />
              <Skeleton className="h-6 w-24 rounded-full" />
            </div>
            <Skeleton className="mt-2 h-10 w-full rounded-lg" />
          </div>
        </div>
        {Array.from({ length: 4 }).map((_, i) => (
          <div
            key={i}
            className="overflow-hidden rounded-2xl bg-white"
          >
            <div className="flex flex-row items-center justify-between gap-2 px-6 pb-3 pt-5">
              <Skeleton className="h-4 w-32" />
              <Skeleton className="h-8 w-20 rounded-full" />
            </div>
            <div className="space-y-3 px-6 pb-6">
              <Skeleton className="h-3 w-full" />
              <Skeleton className="h-3 w-3/4" />
              <Skeleton className="h-3 w-1/2" />
            </div>
          </div>
        ))}
      </section>
    );
  }

  const avatarUrl = absoluteAvatarUrl(user.fotoUrl, fotoUpdatedAt);

  return (
    <section className="mx-auto flex w-full max-w-2xl flex-col gap-5">
      <PageHeader title="Meu Perfil" subtitle="Atualize suas informações e o que você busca no evento." />

      <div className="relative overflow-hidden rounded-2xl bg-white p-6 shadow-[0_1px_2px_rgba(16,24,40,0.05)]">
        <div className="relative flex flex-col items-center gap-4 text-center">
          <div className="relative">
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={avatarMutation.isPending}
              aria-label="Trocar foto de perfil"
              className="group relative overflow-hidden rounded-full shadow-[0_4px_12px_-4px_rgba(16,24,40,0.12)] transition focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-black"
            >
              <UserAvatar nome={user.nome} fotoUrl={avatarUrl} size="xl" />
              <span
                className={cn(
                  'absolute inset-0 flex items-center justify-center rounded-full bg-black/50 text-white opacity-0 transition-opacity',
                  avatarMutation.isPending ? 'opacity-100' : 'group-hover:opacity-100',
                )}
              >
                {avatarMutation.isPending ? (
                  <Loader2 className="h-6 w-6 animate-spin" />
                ) : (
                  <Camera className="h-6 w-6" />
                )}
              </span>
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif"
              className="hidden"
              onChange={handleAvatarChange}
            />
          </div>
          <div>
            <p className="text-xl font-semibold tracking-tight text-foreground">
              {user.nome || 'Sem nome'}
            </p>
            <p className="mt-0.5 text-sm text-muted-foreground">
              {user.cargo ?? 'Cargo não informado'}
              {user.empresa ? ` · ${user.empresa}` : ''}
            </p>
            <div className="mt-2 flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
              {user.segmento ? (
                <span className="inline-flex items-center gap-1">
                  <Building2 className="h-3 w-3" /> {user.segmento}
                </span>
              ) : null}
              {(user.cidade || user.estado) ? (
                <span className="inline-flex items-center gap-1">
                  <MapPin className="h-3 w-3" />
                  {[user.cidade, user.estado].filter(Boolean).join(', ')}
                </span>
              ) : null}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant={user.plano === 'PREMIUM' ? 'success' : 'secondary'}>{user.plano}</Badge>
            {user.onboardingCompleto ? (
              <Badge variant="outline" className="gap-1">
                <ShieldCheck className="h-3 w-3" /> Onboarding OK
              </Badge>
            ) : null}
          </div>
          {/* QR Code ocultado temporariamente (sera reativado para credenciamento no evento). */}
        </div>
      </div>

      <EditableSection
        title="Dados pessoais"
        editing={editing === 'pessoal'}
        onEdit={() => setEditing('pessoal')}
        onCancel={() => setEditing(null)}
        viewContent={
          <>
            <Row label="Nome" value={user.nome ?? '—'} />
            <Row label="Email" value={user.email} leadingIcon={<Mail className="h-3.5 w-3.5" />} />
            <Row
              label="WhatsApp"
              value={user.whatsapp ? formatPhoneBR(user.whatsapp) : 'Não informado'}
            />
          </>
        }
      >
        <PersonalForm user={user} onDone={() => setEditing(null)} />
      </EditableSection>

      <EditableSection
        title="Empresa"
        editing={editing === 'empresa'}
        onEdit={() => setEditing('empresa')}
        onCancel={() => setEditing(null)}
        viewContent={
          <>
            <Row label="Empresa" value={user.empresa ?? '—'} />
            <Row label="Cargo" value={user.cargo ?? '—'} />
            <Row label="Segmento" value={user.segmento ?? '—'} />
            <Row
              label="Localização"
              value={[user.cidade, user.estado].filter(Boolean).join(', ') || '—'}
            />
            <Row
              label="Perfil"
              value={
                TIPO_PERFIL_OPTIONS.find((t) => t.value === user.tipoPerfil)?.label ?? '—'
              }
            />
          </>
        }
      >
        <CompanyForm user={user} onDone={() => setEditing(null)} />
      </EditableSection>

      <EditableSection
        title="Tags do match"
        editing={editing === 'tags'}
        onEdit={() => setEditing('tags')}
        onCancel={() => setEditing(null)}
        viewContent={
          <div className="space-y-3">
            <div>
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                O que busco
              </p>
              <div className="flex flex-wrap gap-2">
                {(user.tagsBusca ?? []).length === 0 ? (
                  <p className="text-sm text-muted-foreground">Nenhuma tag selecionada.</p>
                ) : (
                  (user.tagsBusca ?? []).map((t) => (
                    <Chip key={t.id} tone="busca">
                      {t.nome}
                    </Chip>
                  ))
                )}
              </div>
            </div>
            <div>
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                O que ofereço
              </p>
              <div className="flex flex-wrap gap-2">
                {(user.tagsOferta ?? []).length === 0 ? (
                  <p className="text-sm text-muted-foreground">Nenhuma tag selecionada.</p>
                ) : (
                  (user.tagsOferta ?? []).map((t) => (
                    <Chip key={t.id} tone="oferta">
                      {t.nome}
                    </Chip>
                  ))
                )}
              </div>
            </div>
          </div>
        }
      >
        <TagsForm user={user} onDone={() => setEditing(null)} />
      </EditableSection>

      <EditableSection
        title="Objetivo no evento"
        editing={editing === 'bio'}
        onEdit={() => setEditing('bio')}
        onCancel={() => setEditing(null)}
        viewContent={
          <Row
            label="Maior desafio hoje"
            value={user.gargalo ?? 'Compartilhe seu maior gargalo para receber matches mais precisos.'}
          />
        }
      >
        <BioForm user={user} onDone={() => setEditing(null)} />
      </EditableSection>

      <div className="overflow-hidden rounded-2xl bg-white p-4 shadow-[0_1px_2px_rgba(16,24,40,0.05)]">
        <Button
          variant="destructive"
          fullWidth
          leftIcon={<LogOut className="h-4 w-4" />}
          onClick={handleLogout}
          loading={signingOut}
        >
          Sair da conta
        </Button>
      </div>

      {/* Sheet do QR Code removida temporariamente. */}
    </section>
  );
};

const EditableSection = ({
  title,
  editing,
  onEdit,
  onCancel,
  viewContent,
  children,
}: {
  title: string;
  editing: boolean;
  onEdit: () => void;
  onCancel: () => void;
  viewContent: React.ReactNode;
  children: React.ReactNode;
}) => (
  <div className="overflow-hidden rounded-2xl bg-white shadow-[0_1px_2px_rgba(16,24,40,0.05)]">
    <div className="flex flex-row items-center justify-between gap-2 px-6 pb-3 pt-5">
      <h2 className="text-base font-semibold tracking-tight text-gray-900">{title}</h2>
      {editing ? (
        <button
          type="button"
          onClick={onCancel}
          aria-label="Cancelar edição"
          className="inline-flex h-8 items-center gap-1 rounded-full px-3 text-xs font-medium text-gray-500 transition hover:bg-gray-50 hover:text-gray-900"
        >
          <X className="h-3.5 w-3.5" /> Cancelar
        </button>
      ) : (
        <button
          type="button"
          onClick={onEdit}
          aria-label={`Editar ${title}`}
          className="inline-flex h-8 items-center gap-1 rounded-full px-3 text-xs font-medium text-gray-600 transition hover:bg-gray-50 hover:text-gray-900"
        >
          <Pencil className="h-3.5 w-3.5" /> Editar
        </button>
      )}
    </div>
    <div className="space-y-3 px-6 pb-6 text-sm">
      {editing ? children : viewContent}
    </div>
  </div>
);

const Row = ({
  label,
  value,
  leadingIcon,
}: {
  label: string;
  value: string;
  leadingIcon?: React.ReactNode;
}) => (
  <div className="flex items-start justify-between gap-3">
    <span className="shrink-0 text-xs text-muted-foreground">{label}</span>
    <span className="inline-flex items-center gap-1.5 text-right text-sm font-medium text-foreground">
      {leadingIcon}
      {value}
    </span>
  </div>
);

const Field = ({
  label,
  children,
  hint,
}: {
  label: string;
  children: React.ReactNode;
  hint?: string;
}) => (
  <label className="flex flex-col gap-1.5 text-xs">
    <span className="font-medium text-muted-foreground">{label}</span>
    {children}
    {hint ? <span className="text-[11px] text-muted-foreground">{hint}</span> : null}
  </label>
);

const inputClass =
  'h-10 w-full rounded-lg border border-border bg-background px-3 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

const useUpdateUser = (onDone: () => void, successMessage: string) => {
  const qc = useQueryClient();
  const setUser = useAuthStore((s) => s.setUser);
  return useMutation({
    mutationFn: (payload: UpdateProfilePayload) => userApi.updateMe(payload),
    onSuccess: ({ user: next }) => {
      // setQueryData sincrono evita o flash de dado antigo enquanto o
      // invalidateQueries dispara refetch async.
      qc.setQueryData(QUERY_KEYS.ME, { user: next });
      setUser({
        id: next.id,
        email: next.email,
        nome: next.nome,
        fotoUrl: next.fotoUrl,
        onboardingCompleto: next.onboardingCompleto,
        plano: next.plano,
      });
      void qc.invalidateQueries({ queryKey: QUERY_KEYS.ME });
      toast.success(successMessage);
      onDone();
    },
    onError: (err: Error) => toast.error(err.message || 'Falha ao salvar'),
  });
};

const PersonalForm = ({ user, onDone }: { user: User; onDone: () => void }) => {
  const [nome, setNome] = useState(user.nome ?? '');
  // Persistimos digitos puros no estado interno; mascara so na UI.
  // Inicializa via formatPhoneBR para mostrar valor salvo formatado mesmo
  // que o backend tenha armazenado misturado (ex.: registros antigos com
  // mascara). onlyDigits no submit garante que enviamos sempre 10 ou 11
  // digitos limpos para o backend.
  const [whatsapp, setWhatsapp] = useState(formatPhoneBR(user.whatsapp ?? ''));
  const mutation = useUpdateUser(onDone, 'Dados atualizados');
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (nome.trim().length < 2) {
      toast.error('Nome precisa ter ao menos 2 caracteres');
      return;
    }
    const digits = onlyDigits(whatsapp);
    if (digits.length > 0 && digits.length < 10) {
      toast.error('Digite o WhatsApp completo (DDD + numero).');
      return;
    }
    // Backend nao aceita null (DTO @IsString); usa undefined para "nao
    // tocar" e string vazia para "limpar". Quando usuario apaga um
    // whatsapp pre-existente, mandamos string vazia.
    const whatsappToSend =
      digits.length > 0 ? digits : user.whatsapp ? '' : undefined;
    mutation.mutate({ nome: nome.trim(), whatsapp: whatsappToSend });
  };
  return (
    <form className="flex flex-col gap-3" onSubmit={handleSubmit}>
      <Field label="Nome completo">
        <input
          className={inputClass}
          value={nome}
          onChange={(e) => setNome(e.target.value)}
          maxLength={120}
          required
        />
      </Field>
      <Field label="Email" hint="O email não pode ser alterado pelo perfil.">
        <input className={cn(inputClass, 'cursor-not-allowed bg-muted')} value={user.email} readOnly />
      </Field>
      <Field label="WhatsApp" hint="Compartilhado só com matches mútuos.">
        <input
          className={inputClass}
          value={whatsapp}
          onChange={(e) => setWhatsapp(formatPhoneBR(e.target.value))}
          placeholder="(67) 99999-0000"
          inputMode="tel"
          autoComplete="tel-national"
          maxLength={16}
        />
      </Field>
      <SaveButton pending={mutation.isPending} />
    </form>
  );
};

const CompanyForm = ({ user, onDone }: { user: User; onDone: () => void }) => {
  const [empresa, setEmpresa] = useState(user.empresa ?? '');
  const [cargo, setCargo] = useState(user.cargo ?? '');
  const [segmento, setSegmento] = useState(user.segmento ?? '');
  const [cidade, setCidade] = useState(user.cidade ?? '');
  const [estado, setEstado] = useState(user.estado ?? '');
  const [tipoPerfil, setTipoPerfil] = useState(user.tipoPerfil ?? 'EMPRESARIO');
  const mutation = useUpdateUser(onDone, 'Dados da empresa atualizados');
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    mutation.mutate({
      empresa: empresa.trim() || undefined,
      cargo: cargo.trim() || undefined,
      segmento: segmento.trim() || undefined,
      cidade: cidade.trim() || undefined,
      estado: estado.trim().toUpperCase().slice(0, 2) || undefined,
      tipoPerfil: tipoPerfil as UpdateProfilePayload['tipoPerfil'],
    });
  };
  return (
    <form className="flex flex-col gap-3" onSubmit={handleSubmit}>
      <Field label="Empresa">
        <input
          className={inputClass}
          value={empresa}
          onChange={(e) => setEmpresa(e.target.value)}
          maxLength={160}
        />
      </Field>
      <Field label="Cargo">
        <input
          className={inputClass}
          value={cargo}
          onChange={(e) => setCargo(e.target.value)}
          maxLength={120}
        />
      </Field>
      <Field label="Segmento">
        <input
          className={inputClass}
          value={segmento}
          onChange={(e) => setSegmento(e.target.value)}
          maxLength={120}
          placeholder="Ex.: Tecnologia, Agronegócio"
        />
      </Field>
      <div className="grid grid-cols-[1fr_88px] gap-3">
        <Field label="Cidade">
          <input
            className={inputClass}
            value={cidade}
            onChange={(e) => setCidade(e.target.value)}
            maxLength={120}
          />
        </Field>
        <Field label="UF">
          <select
            className={inputClass}
            value={estado}
            onChange={(e) => setEstado(e.target.value)}
          >
            <option value="">—</option>
            {ESTADOS_UF.map((uf) => (
              <option key={uf} value={uf}>
                {uf}
              </option>
            ))}
          </select>
        </Field>
      </div>
      <Field label="Como você se identifica">
        <select
          className={inputClass}
          value={tipoPerfil ?? ''}
          onChange={(e) => setTipoPerfil(e.target.value as typeof tipoPerfil)}
        >
          {TIPO_PERFIL_OPTIONS.map((t) => (
            <option key={t.value} value={t.value}>
              {t.label}
            </option>
          ))}
        </select>
      </Field>
      <SaveButton pending={mutation.isPending} />
    </form>
  );
};

const TagsForm = ({ user, onDone }: { user: User; onDone: () => void }) => {
  const { data: tagsData, isLoading } = useQuery({
    queryKey: QUERY_KEYS.TAGS,
    queryFn: tagApi.list,
    staleTime: 60 * 60 * 1000,
  });

  // Mapeia nomes -> ids reais do catalogo (o user vem com id=nome do nosso adapter).
  const nameToId = useMemo(() => {
    const map = new Map<string, string>();
    (tagsData?.tags ?? []).forEach((t) => map.set(t.nome, t.id));
    return map;
  }, [tagsData]);

  const initialBusca = useMemo(
    () =>
      (user.tagsBusca ?? [])
        .map((t) => nameToId.get(t.nome))
        .filter((id): id is string => Boolean(id)),
    [user.tagsBusca, nameToId],
  );
  const initialOferta = useMemo(
    () =>
      (user.tagsOferta ?? [])
        .map((t) => nameToId.get(t.nome))
        .filter((id): id is string => Boolean(id)),
    [user.tagsOferta, nameToId],
  );

  const [busca, setBusca] = useState<string[]>([]);
  const [oferta, setOferta] = useState<string[]>([]);
  useEffect(() => setBusca(initialBusca), [initialBusca]);
  useEffect(() => setOferta(initialOferta), [initialOferta]);

  // O catalogo de tags eh unico no backend; usuario pode escolher qualquer
  // tag para busca OU oferta. O campo `categoria` retornado pelo backend eh
  // apenas rotulo tematico (financeiro, vendas, tech), nao semantica de uso.
  const tagsCatalog = tagsData?.tags ?? [];

  const qc = useQueryClient();
  const updateTagsMutation = useMutation({
    mutationFn: async ({ buscaIds, ofertaIds }: { buscaIds: string[]; ofertaIds: string[] }) => {
      // Reuse onboarding endpoint para replace atomico das tags. O perfil ja
      // existe; este endpoint apenas substitui o conjunto de tags.
      // Sem endpoint dedicado, este eh o caminho mais limpo no momento.
      const profile = {
        tipoPerfil: (user.tipoPerfil ?? 'EMPRESARIO') as 'EMPRESARIO',
        cargo: user.cargo ?? '',
        empresa: user.empresa ?? '',
        segmento: user.segmento ?? '',
        estado: user.estado ?? 'SP',
        faturamento: (user.faturamento ?? 'ATE_500K') as 'ATE_500K',
        tamanhoTime: user.tamanhoTime ?? 1,
        frequenciaNetworking: 'MEDIA' as const,
        gargalo: user.gargalo ?? '',
        diagnosticoFase: 'CRESCIMENTO' as const,
        consentSponsor: user.consentSponsor ?? false,
        tagsBuscaIds: buscaIds,
        tagsOfertaIds: ofertaIds,
      };
      return userApi.submitOnboarding(profile);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: QUERY_KEYS.ME });
      toast.success('Tags atualizadas');
      onDone();
    },
    onError: (err: Error) => toast.error(err.message || 'Falha ao salvar tags'),
  });

  const toggle = (list: string[], set: (v: string[]) => void, id: string) => {
    if (list.includes(id)) set(list.filter((x) => x !== id));
    else if (list.length < MAX_TAGS) set([...list, id]);
    else toast(`Máximo ${MAX_TAGS} tags`, { description: 'Remova uma para escolher outra.' });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    updateTagsMutation.mutate({ buscaIds: busca, ofertaIds: oferta });
  };

  if (isLoading) {
    return <p className="text-sm text-muted-foreground">Carregando catálogo de tags...</p>;
  }

  return (
    <form className="flex flex-col gap-5" onSubmit={handleSubmit}>
      <TagsSelector
        title="O que busco"
        tone="busca"
        max={MAX_TAGS}
        selected={busca}
        options={tagsCatalog}
        onToggle={(id) => toggle(busca, setBusca, id)}
      />
      <TagsSelector
        title="O que ofereço"
        tone="oferta"
        max={MAX_TAGS}
        selected={oferta}
        options={tagsCatalog}
        onToggle={(id) => toggle(oferta, setOferta, id)}
      />
      <SaveButton pending={updateTagsMutation.isPending} />
    </form>
  );
};

const TagsSelector = ({
  title,
  tone,
  max,
  selected,
  options,
  onToggle,
}: {
  title: string;
  tone: 'busca' | 'oferta';
  max: number;
  selected: string[];
  options: { id: string; nome: string }[];
  onToggle: (id: string) => void;
}) => (
  <div>
    <div className="mb-2 flex items-center justify-between">
      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {title}
      </p>
      <span className="text-[11px] text-muted-foreground">
        {selected.length}/{max}
      </span>
    </div>
    <div className="flex flex-wrap gap-1.5">
      {options.map((t) => {
        const active = selected.includes(t.id);
        return (
          <button
            key={t.id}
            type="button"
            onClick={() => onToggle(t.id)}
            aria-pressed={active}
            className={cn(
              'inline-flex h-8 items-center gap-1 rounded-full border px-3 text-xs font-medium transition-colors',
              active
                ? tone === 'busca'
                  ? 'border-[#175CD3] bg-[#175CD3] text-white'
                  : 'border-[#067647] bg-[#067647] text-white'
                : 'border-border bg-background text-foreground hover:bg-muted',
            )}
          >
            {active ? <Check className="h-3 w-3" /> : null}
            {t.nome}
          </button>
        );
      })}
    </div>
  </div>
);

const BioForm = ({ user, onDone }: { user: User; onDone: () => void }) => {
  const [gargalo, setGargalo] = useState(user.gargalo ?? '');
  const mutation = useUpdateUser(onDone, 'Objetivo atualizado');
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    mutation.mutate({ gargalo: gargalo.trim() || undefined });
  };
  return (
    <form className="flex flex-col gap-3" onSubmit={handleSubmit}>
      <Field label="Maior desafio hoje" hint="Quanto mais específico, melhor o algoritmo de match.">
        <textarea
          className={cn(inputClass, 'min-h-[88px] resize-y py-2.5')}
          value={gargalo}
          onChange={(e) => setGargalo(e.target.value)}
          maxLength={500}
          placeholder="Ex.: Quero entender como escalar vendas B2B no centro-oeste."
        />
      </Field>
      <SaveButton pending={mutation.isPending} />
    </form>
  );
};

const SaveButton = ({ pending }: { pending: boolean }) => (
  <button
    type="submit"
    disabled={pending}
    className="inline-flex h-10 items-center justify-center gap-2 self-end rounded-lg bg-foreground px-5 text-sm font-semibold text-background transition hover:bg-foreground/90 disabled:opacity-60"
  >
    {pending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
    {pending ? 'Salvando…' : 'Salvar'}
  </button>
);
